import React, { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import dayjs from "dayjs";
import {
  Download,
  Eye,
  Filter,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { Switch } from "~/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useManualFacturas } from "~/hooks/useManualFacturas";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { formatCurrency, formatRut } from "~/utils/helpers";

// Payment assignment form schema
const paymentFormSchema = z.object({
  amount: z
    .number()
    .min(1, "El monto debe ser mayor a 0")
    .refine((val) => !isNaN(val), {
      message: "El monto debe ser un número válido",
    }),
  reference: z.string().optional(),
});

type PaymentFormValues = z.infer<typeof paymentFormSchema>;

// Payment assignment dialog component
interface PaymentAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  factura: any;
  onPaymentAssigned: () => void;
}

const PaymentAssignmentDialog: React.FC<PaymentAssignmentDialogProps> = ({
  open,
  onOpenChange,
  factura,
  onPaymentAssigned,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      amount: undefined,
      reference: "",
    },
  });

  const onSubmit = async (values: PaymentFormValues) => {
    setIsSubmitting(true);
    try {
      // First, create the payment record
      const { data: paymentData, error: paymentError } = await supabase
        .from("payments")
        .insert({
          amount: values.amount,
          client: factura.client_id,
          manual_factura_id: factura.id,
          payment_method: "manual",
          reference:
            values.reference ||
            "Pago manual registrado desde Cuentas por Cobrar",
        })
        .select();

      if (paymentError) {
        console.error("Error creating payment:", paymentError);
        toast.error("Error al registrar el pago");
        return;
      }

      // Calculate new paid amount using RPC function that excludes checks in portfolio
      const { data: newPaidAmount, error: paidAmountError } =
        await supabase.rpc("get_manual_factura_paid_amount", {
          factura_id: factura.id,
        });

      if (paidAmountError) {
        console.error("Error calculating paid amount:", paidAmountError);
        toast.error("Error al calcular el monto pagado");
        return;
      }

      const duesAmount = factura.dues || 0;
      const newBalance = duesAmount - (newPaidAmount || 0);

      // Update the manual factura with new paid amount and balance
      const { error: updateError } = await supabase
        .from("manual_facturas")
        .update({
          paid: newPaidAmount || 0,
          balance: newBalance,
        })
        .eq("id", factura.id);

      if (updateError) {
        console.error("Error updating manual factura:", updateError);
        // If update fails, try to rollback the payment
        await supabase.from("payments").delete().eq("id", paymentData[0].id);
        toast.error("Error al actualizar la factura");
        return;
      }

      toast.success("Pago registrado exitosamente");
      form.reset();
      onOpenChange(false);
      onPaymentAssigned();
    } catch (error) {
      console.error("Error in payment submission:", error);
      toast.error("Error al procesar el pago");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate maximum amount that can be paid (current balance)
  const maxAmount = factura?.balance || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Asignar Pago Manual</DialogTitle>
          <DialogDescription>
            Registrar un pago manual para la factura{" "}
            {factura?.doc_number || "sin número"} del cliente{" "}
            {factura?.client_name}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-4">
            {/* Factura Information */}
            <div className="space-y-2 rounded-lg bg-gray-50 p-3">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Documento:</span>
                <span className="text-sm">
                  {factura?.doc_number || "Sin número"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Saldo Pendiente:</span>
                <span className="text-sm font-bold text-red-600">
                  ${formatCurrency(maxAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Total Adeudado:</span>
                <span className="text-sm">
                  ${formatCurrency(factura?.dues || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Pagado:</span>
                <span className="text-sm text-green-600">
                  ${formatCurrency(factura?.paid || 0)}
                </span>
              </div>
            </div>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monto del Pago</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="1"
                          max={maxAmount}
                          placeholder="Ingrese el monto"
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(
                              parseFloat(e.target.value) || undefined
                            )
                          }
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500">
                        Máximo: ${formatCurrency(maxAmount)}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Referencia (Opcional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Descripción del pago" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !form.formState.isValid}
                  >
                    {isSubmitting ? "Registrando..." : "Registrar Pago"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Pagination component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const pageNumbers = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  if (totalPages <= 1) return null;

  return (
    <div className="mt-4 flex items-center justify-center space-x-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
      >
        Anterior
      </Button>

      {startPage > 1 && (
        <>
          <Button variant="outline" size="sm" onClick={() => onPageChange(1)}>
            1
          </Button>
          {startPage > 2 && <span className="px-2">...</span>}
        </>
      )}

      {pageNumbers.map((page) => (
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(page)}
        >
          {page}
        </Button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && <span className="px-2">...</span>}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </Button>
        </>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
      >
        Siguiente
      </Button>
    </div>
  );
};

// Function to get document type badge
const getDocTypeBadge = (docType: string | null) => {
  switch (docType) {
    case "FV":
      return (
        <Badge variant="outline" className="border-blue-600 text-blue-600">
          Factura de Venta
        </Badge>
      );
    case "PL":
      return (
        <Badge variant="outline" className="border-green-600 text-green-600">
          Pagare
        </Badge>
      );
    case "NC":
      return (
        <Badge variant="outline" className="border-purple-600 text-purple-600">
          Nota de Crédito
        </Badge>
      );
    case "ND":
      return (
        <Badge variant="outline" className="border-orange-600 text-orange-600">
          Nota de Débito
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="border-gray-600 text-gray-600">
          {docType || "N/A"}
        </Badge>
      );
  }
};

// Function to get payment status badge
const getPaymentStatusBadge = (
  dues: number | null,
  paid: number | null,
  balance: number | null
) => {
  const duesAmount = dues || 0;
  const paidAmount = paid || 0;
  const balanceAmount = balance || 0;

  if (balanceAmount <= 0) {
    return (
      <Badge
        variant="outline"
        className="border-green-600 bg-green-50 text-green-600"
      >
        Pagado
      </Badge>
    );
  } else if (paidAmount > 0) {
    return (
      <Badge
        variant="outline"
        className="border-yellow-600 bg-yellow-50 text-yellow-600"
      >
        Pago Parcial
      </Badge>
    );
  } else {
    return (
      <Badge
        variant="outline"
        className="border-red-600 bg-red-50 text-red-600"
      >
        Pendiente
      </Badge>
    );
  }
};

const CuentasPorCobrarTable: React.FC<{ showHeader?: boolean }> = ({
  showHeader = true,
}) => {
  const {
    manualFacturas,
    loading,
    searchTerm,
    setSearchTerm,
    refreshManualFacturas,
  } = useManualFacturas();

  // Get current user profile
  const { role, name, lastname } = useProfileStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [docTypeFilter, setDocTypeFilter] = useState<string>("all");
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState<any>(null);
  const itemsPerPage = 10;
  const [loadingExport, setLoadingExport] = useState(false);

  // Get current user's full name for seller filtering
  const currentUserFullName =
    name && lastname ? `${name} ${lastname}`.trim() : null;
  const isSeller = role === "seller";

  // Apply all filters
  const filteredFacturas = useMemo(() => {
    return manualFacturas.filter((factura) => {
      // Seller filter - if current user is a seller, only show their clients
      if (
        isSeller &&
        currentUserFullName &&
        factura.client_seller !== currentUserFullName
      ) {
        return false;
      }

      // Document type filter
      if (docTypeFilter !== "all" && factura.doc_type !== docTypeFilter) {
        return false;
      }

      // Expiration filter
      if (showExpiredOnly) {
        const isOverdue =
          factura.payment_due_date &&
          new Date() > new Date(factura.payment_due_date) &&
          (factura.balance || 0) !== 0;
        if (!isOverdue) {
          return false;
        }
      }

      return true;
    });
  }, [
    manualFacturas,
    docTypeFilter,
    showExpiredOnly,
    isSeller,
    currentUserFullName,
  ]);

  // Calculate pagination based on filtered data
  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredFacturas.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filteredFacturas.length / itemsPerPage);

    return { paginatedData, totalPages };
  }, [filteredFacturas, currentPage, itemsPerPage]);

  // Get unique document types for filter dropdown
  const documentTypes = useMemo(() => {
    const types = new Set(
      manualFacturas.map((f) => f.doc_type).filter(Boolean)
    );
    return Array.from(types).sort();
  }, [manualFacturas]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, docTypeFilter, showExpiredOnly]);

  // Clear all filters
  const clearFilters = () => {
    setDocTypeFilter("all");
    setShowExpiredOnly(false);
    setSearchTerm("");
  };

  // Check if any filters are active
  const hasActiveFilters =
    docTypeFilter !== "all" || showExpiredOnly || searchTerm;

  // Handle payment assignment
  const handleAssignPayment = (factura: any) => {
    setSelectedFactura(factura);
    setPaymentDialogOpen(true);
  };

  const handlePaymentAssigned = () => {
    refreshManualFacturas();

    // Dispatch events to update related components (like ClientHeader debt alerts)
    window.dispatchEvent(new CustomEvent("manual-facturas-updated"));

    if (selectedFactura?.client_id) {
      window.dispatchEvent(
        new CustomEvent("payment-assigned-to-manual-factura", {
          detail: { clientId: selectedFactura.client_id },
        })
      );
    }
  };

  const handleExportToExcel = async () => {
    setLoadingExport(true);
    try {
      // Prepare flat export data
      const exportData = filteredFacturas.map((factura) => ({
        "Nro. Documento": factura.doc_number,
        "Cod. Cliente": formatRut(factura.client_rut),
        "Nombre Cliente": factura.client_name,
        "Fecha Emisión": dayjs(factura.created_at).format("DD/MM/YYYY"),
        "Fecha Vencimiento": dayjs(factura.payment_due_date).format(
          "DD/MM/YYYY"
        ),
        "Monto Original": factura.dues,
        Saldo: factura.balance,
        "Nombre Vendedor": factura.client_seller || "Sin Vendedor",
      }));

      // Sort by document number
      exportData.sort((a, b) => {
        if (a["Nombre Cliente"] < b["Nombre Cliente"]) return -1;
        if (a["Nombre Cliente"] > b["Nombre Cliente"]) return 1;
        return 0;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      worksheet["!cols"] = [
        { wch: 15 }, // Nro. Documento
        { wch: 15 }, // Cod. Cliente
        { wch: 40 }, // Nombre Cliente
        { wch: 15 }, // Fecha Emisión
        { wch: 15 }, // Fecha Venci
        { wch: 15 }, // Monto Origi
        { wch: 15 }, // Saldo
        { wch: 30 }, // Nombre Vendedor
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Facturas");
      XLSX.writeFile(workbook, "facturas.xlsx");
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingExport(false);
    }
  };

  if (loading) {
    return (
      <Card>
        {showHeader && (
          <CardHeader>
            <CardTitle>Cuentas por Cobrar</CardTitle>
          </CardHeader>
        )}
        <CardContent className="p-0">
          <div className="space-y-4 p-4">
            <Skeleton className="h-10 w-full" />
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Fecha Vencimiento</TableHead>
                    <TableHead>Monto Adeudado</TableHead>
                    <TableHead>Pagado</TableHead>
                    <TableHead>Saldo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        {showHeader && (
          <CardHeader className="pb-0">
            <CardTitle>Cuentas por Cobrar</CardTitle>
          </CardHeader>
        )}
        <CardHeader>
          <div className="flex flex-col justify-between gap-y-4 lg:flex-row lg:items-center">
            {filteredFacturas.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Mostrando {paginatedData.length} de {filteredFacturas.length}{" "}
                registros
                {isSeller && " (solo sus clientes asignados)"}
                {hasActiveFilters &&
                  ` (filtrado de ${manualFacturas.length} total)`}
              </p>
            )}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex w-full flex-col gap-2 lg:w-fit lg:flex-row lg:items-center">
                  <div className="relative w-full flex-1 lg:max-w-xs">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                    <Input
                      placeholder="Buscar por cliente, RUT o documento..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 ${hasActiveFilters ? "border-blue-200 bg-blue-50" : ""}`}
                  >
                    <Filter className="h-4 w-4" />
                    Filtros
                    {hasActiveFilters && (
                      <Badge
                        variant="secondary"
                        className="ml-1 flex h-5 w-5 items-center justify-center rounded-full p-0 text-xs"
                      >
                        {
                          [
                            docTypeFilter !== "all",
                            showExpiredOnly,
                            searchTerm,
                          ].filter(Boolean).length
                        }
                      </Badge>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshManualFacturas}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Actualizar
                  </Button>
                  <Button
                    onClick={async () => await handleExportToExcel()}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    disabled={loadingExport}
                  >
                    {loadingExport ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Exportando...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Descargar Excel
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="rounded-lg border bg-gray-50 p-4">
                  <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="doc-type-filter">Tipo de Documento</Label>
                      <Select
                        value={docTypeFilter}
                        onValueChange={setDocTypeFilter}
                      >
                        <SelectTrigger
                          id="doc-type-filter"
                          className="w-full sm:w-48"
                        >
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los tipos</SelectItem>
                          {documentTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type === "FV"
                                ? "Factura de Venta"
                                : type === "PL"
                                  ? "Pagare"
                                  : type === "NC"
                                    ? "Nota de Crédito"
                                    : type === "ND"
                                      ? "Nota de Débito"
                                      : type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="expired-filter"
                        checked={showExpiredOnly}
                        onCheckedChange={setShowExpiredOnly}
                      />
                      <Label htmlFor="expired-filter">Solo vencidos</Label>
                    </div>

                    {hasActiveFilters && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearFilters}
                        className="flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Limpiar filtros
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredFacturas.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>
                {isSeller
                  ? "No se encontraron cuentas por cobrar para sus clientes asignados"
                  : "No se encontraron cuentas por cobrar"}
              </p>
              {hasActiveFilters ? (
                <div className="mt-2">
                  <p className="text-sm">
                    Intenta ajustar los filtros o términos de búsqueda
                  </p>
                  <Button
                    variant="link"
                    onClick={clearFilters}
                    className="mt-1 text-sm text-blue-600"
                  >
                    Limpiar todos los filtros
                  </Button>
                </div>
              ) : (
                <p className="mt-2 text-sm">
                  {isSeller
                    ? "No tiene clientes con deudas pendientes"
                    : "No hay datos disponibles"}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Fecha Vencimiento</TableHead>
                      <TableHead>Monto Adeudado</TableHead>
                      <TableHead>Pagado</TableHead>
                      <TableHead>Saldo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((factura, index) => {
                      const isOverdue =
                        factura.payment_due_date &&
                        new Date() > new Date(factura.payment_due_date) &&
                        (factura.balance || 0) !== 0;

                      return (
                        <TableRow
                          key={factura.id}
                          style={{
                            animationDelay: `${Math.min(index * 40, 400)}ms`,
                          }}
                          className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${
                            isOverdue ? "bg-red-50 hover:bg-red-100" : ""
                          }`}
                        >
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {factura.client_name}
                              </div>
                              {factura.client_tax_id && (
                                <div className="text-xs text-gray-500">
                                  {factura.client_tax_id}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {factura.doc_number || "N/A"}
                              </div>
                              {factura.description && (
                                <div className="max-w-32 truncate text-xs text-gray-500">
                                  {factura.description}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {getDocTypeBadge(factura.doc_type)}
                          </TableCell>
                          <TableCell>
                            {factura.payment_due_date ? (
                              <div
                                className={
                                  isOverdue ? "font-medium text-red-600" : ""
                                }
                              >
                                {format(
                                  new Date(factura.payment_due_date),
                                  "dd MMM yyyy",
                                  { locale: es }
                                )}
                                {isOverdue && (
                                  <div className="text-xs text-red-500">
                                    Vencido
                                  </div>
                                )}
                              </div>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">
                              ${formatCurrency(factura.dues || 0)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-green-600">
                              ${formatCurrency(factura.paid || 0)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`font-medium ${
                                (factura.balance || 0) > 0
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              ${formatCurrency(factura.balance || 0)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {getPaymentStatusBadge(
                              factura.dues,
                              factura.paid,
                              factura.balance
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-1"
                                onClick={() => {
                                  // Open client detail page in new tab
                                  window.open(
                                    `/clients/${factura.client_id}`,
                                    "_blank"
                                  );
                                }}
                              >
                                <Eye className="h-4 w-4" />
                                Ver datos de cliente
                              </Button>
                              {(factura.balance || 0) > 0 && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  onClick={() => handleAssignPayment(factura)}
                                >
                                  <Plus className="h-4 w-4" />
                                  Asignar Pago
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="p-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Payment Assignment Dialog */}
      <PaymentAssignmentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        factura={selectedFactura}
        onPaymentAssigned={handlePaymentAssigned}
      />
    </>
  );
};

export default CuentasPorCobrarTable;
